import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import { required, maxLength } from "@vuelidate/validators";
import { insertConversation, 
  insertGeneralNotice, 
  getConversation, 
  getConversations, 
  getConversationContacts, 
  getConversationMessages, 
  insertConversationMessage, 
  getAvailableComponentLookups,
  setConversationStatus,
  markConversationAsRead } from '@/services/ConversationsApiService';
import { useAppStore } from '@/stores/appStore';
import { isSameDate, formatDate } from '@/utils/dataUtils';
import { ATTRIBUTE_TYPES, CONVERSATION_TYPES, BASE_STORAGE_KEY } from '@/utils/constants';
import { useAuthStore} from '@/stores/authStore';
import SecureLS from 'secure-ls';

const LOCAL_STORAGE_KEY = `${BASE_STORAGE_KEY}:conversations`;

const ls = new SecureLS({ encodingType: 'aes', storage: sessionStorage  });

export const useConversationStore = defineStore(LOCAL_STORAGE_KEY, () => {
  const { getAttributeIdByTypeAndTextValue1, getAttributeValues } = useAppStore();
  const authStore = useAuthStore();
  // State
  const organizationId = ref(null);
  const participantIds = ref(null);
  const hccisId = ref(null);
  const organizationTypeId = ref(null);
  const organizationName = ref(null);
  const conversationId = ref(null);
  const threadId = ref(null);
  const conversationSubject = ref('');
  const conversationMessage = ref(null);
  const conversationMessageBody = ref(null);
  const conversationTypeId = ref(null);
  const exceptionId = ref(null);
  const componentIds = ref(null);
  const isClosed = ref(false);
  const isRead = ref(false);
  const isNewConversation = ref(true);
  const organizationTypes = ref([]);
  const availableMessageNotes = ref([]);
  const conversationMessages = ref([]);
  const conversationContacts = ref([]);
  const createUser = ref(null);
  const createDate = ref(null);
  const newMessage = ref('');

  const conversations = ref([]);
  const conversationsCriteria = ref({
    startDate: null,
    endDate: null,
  });

  const isGeneralNotice = computed(() => {
    return conversationTypeId.value === getAttributeIdByTypeAndTextValue1(ATTRIBUTE_TYPES.ConversationType, CONVERSATION_TYPES.GeneralNotice);
  });

const canAddMessage = computed(() => {
  // Cannot add message if conversation is closed
  if (isClosed.value) {
    return false;
  }

  // If not a general notice, can always add message
  if (!isGeneralNotice.value) {
    return true;
  }

  // For general notices:
  // - Admin users can add message only if threadId > 0 (reply to a thread)
  // - Non-admin users can always add message
  if (authStore.user.isAdminUser) {
    return threadId.value > 0;
  }

  return true;
});

  function getValidationRules() {
    return {
      organizationId: { required },
      organizationTypeId: { required },
      conversationTypeId: { required },
      conversationSubject: { required, maxLength: maxLength(255) },
      conversationMessage: { required, maxLength: maxLength(8000) },
      participantIds: { required }
    };
  };

  function getNewGeneralNoticeValidationRules() {
    return {
      organizationTypeId: { required },
      conversationSubject: { required, maxLength: maxLength(255) },
      conversationMessage: { required, maxLength: maxLength(8000) }
    };
  };

  function getConversationMessageValidationRules() {
    return {
      newMessage: { required, maxLength: maxLength(8000) }
    };
  };

  const fetchConversation = async () => {
    if (!conversationId.value) {
      return;
    }
    const [conversationData,
      conversationMessagesData] = await Promise.all([
      getConversation(conversationId.value, threadId.value),
      getConversationMessages(conversationId.value, threadId.value)
    ]);
    setData(conversationData);

    conversationMessages.value = conversationMessagesData;

    if (!isGeneralNotice.value) {
      const conversationContactsData = await getConversationContacts(conversationId.value);
      conversationContacts.value = conversationContactsData;
    }
  };

  const fetchConversations = async (startDate, endDate, forceLoad = false) => {
    if (!startDate || !endDate) {
      conversations.value = [];
      return;
    }

    const isSameStartDate = isSameDate(conversationsCriteria.value.startDate, startDate);
    const isSameEndDate = isSameDate(conversationsCriteria.value.endDate, endDate);

    if (isSameStartDate && isSameEndDate && !forceLoad) {
      return;
    }

    conversationsCriteria.value.startDate = startDate;
    conversationsCriteria.value.endDate = endDate;
    const conversationsData = await getConversations(startDate, endDate, false);
    conversations.value = conversationsData;
  };

  const updateConversationStatus = async (isClosedValue = false) => {
    if (!conversationId.value) {
      throw new Error('Conversation ID is required to set status.');
    }
    await setConversationStatus(conversationId.value, isClosedValue);
    isClosed.value = isClosedValue;
    await fetchConversations(conversationsCriteria.value.startDate, conversationsCriteria.value.endDate, true);
  };

  const setActiveConversationParameters = (conversationIdValue, threadIdValue) => {
    resetStore();
    conversationId.value = conversationIdValue;
    threadId.value = threadIdValue;
 };

  function setData (data) {
    console.log('Conversation Data:', data);
    // organizationId.value = data.organizationId;
    organizationName.value = data.organizationName;
    participantIds.value = data.participantIds || null;
    isClosed.value = data.isClosed;
    hccisId.value = data.hccisId || null;
    conversationSubject.value = data.conversationSubject || null;
    conversationMessage.value = data.conversationMessage || null;
    conversationMessageBody.value = data.conversationMessageBody || null;
    conversationTypeId.value = data.conversationTypeId || null;
    exceptionId.value = data.exceptionId || null;
    componentIds.value = data.componentIds || null;
    createUser.value = data.createUser || null;
    createDate.value = data.createDate || null;
    isNewConversation.value = false;
    isRead.value = data.isRead || false;
  };

  async function submitConversation() {
    const conversationData = {
      organizationIds: organizationId.value,
      participantIds: participantIds.value ? participantIds.value.join(',') : null,
      conversationSubject: conversationSubject.value,
      conversationMessage: conversationMessage.value,
      conversationMessageBody: conversationMessage.value,
      conversationTypeId: conversationTypeId.value,
      exceptionId: exceptionId.value,
      componentIds: componentIds.value ? componentIds.value.join(',') : null,
    };

    await insertConversation(conversationData);
    await refreshConversations();
  }

  async function submitGeneralNotice() {
    const generalNoticeData = {
      organizationTypeId: organizationTypeId.value,
      conversationSubject: conversationSubject.value,
      conversationMessage: conversationMessage.value,
      conversationMessageBody: conversationMessage.value,
    };

    await insertGeneralNotice(generalNoticeData);
    await refreshConversations();
  }

  async function refreshConversations() {
    if (!conversationsCriteria.value || !conversationsCriteria.value.startDate || !conversationsCriteria.value.endDate) {
      return;
    }

    await fetchConversations(conversationsCriteria.value.startDate, conversationsCriteria.value.endDate, true);
  }

  async function saveConversationMessage() {
    const messageData = {
      conversationId: conversationId.value,
      messageBody: newMessage.value,
      messageBodyText: newMessage.value,
      threadId: threadId.value,
      componentIds: componentIds.value ? componentIds.value.join(',') : null,
    };

    threadId.value = await insertConversationMessage(conversationId.value, messageData);

    await Promise.all([
      refreshConversationMessages(),
      refreshConversations()
    ]);
  }

  async function markAsRead(conversationIdValue) {
    if (!conversationIdValue) {
      return;
    }

    await markConversationAsRead(conversationIdValue);
  };

  async function refreshConversationMessages() {
    if (!conversationId.value) {
      return;
    }
    const conversationMessagesData = await getConversationMessages(conversationId.value, threadId.value);
    conversationMessages.value = conversationMessagesData;
  };

  const createdByAndDate = computed(() => {
    return createDate.value ? `${formatDate(createDate.value)} by ${createUser.value || 'N/A'}` : null;
  });

  const participantNames = computed(() => {
    return isGeneralNotice.value ? "General Notice" : conversationContacts.value.map(c => c.contactName).join(', ');
  });

  function loadOrganizationTypes() {
    const businessUnits = getAttributeValues(ATTRIBUTE_TYPES.BusinessUnit);
    organizationTypes.value = businessUnits.filter(orgType => orgType.numericValue1 === 1 || orgType.numericValue1 === 2);
  };

  const initializeNewConversation = (isGeneralNotice = false) => {
    organizationTypeId.value = null;
    conversationId.value = null;
    threadId.value = null;
    organizationId.value = null;
    participantIds.value = null;
    conversationSubject.value = '';
    conversationMessage.value = null;
    conversationMessageBody.value = null;
    conversationTypeId.value = !isGeneralNotice ?  
      getAttributeIdByTypeAndTextValue1(ATTRIBUTE_TYPES.ConversationType, CONVERSATION_TYPES.StandardConversation) : 
      getAttributeIdByTypeAndTextValue1(ATTRIBUTE_TYPES.ConversationType, CONVERSATION_TYPES.GeneralNotice);
    exceptionId.value = null;
    componentIds.value = null;
    isNewConversation.value = true;

    loadOrganizationTypes();
  };

  const initializeNewMessage = () => {
    newMessage.value = '';
  };

  async function loadAvailableMessageNotes() {
    if (!organizationId.value) {
      availableMessageNotes.value = [];
      return;
    }

    availableMessageNotes.value = await getAvailableComponentLookups(organizationId.value);
  };

  function resetStore() {
    organizationId.value = null;
    organizationName.value = null;
    participantIds.value = null;
    organizationTypeId.value = null;
    conversationId.value = null;
    threadId.value = null;
    conversationSubject.value = '';
    conversationMessage.value = null;
    conversationMessageBody.value = null;
    conversationTypeId.value = null;
    exceptionId.value = null;
    componentIds.value = null;
    isNewConversation.value = true;
    organizationTypes.value = [];
    conversationContacts.value = [];
    availableMessageNotes.value = [];
    conversationMessages.value = [];
    isRead.value = false;
    hccisId.value = null;
  };

  return {
    conversationId,
    threadId,
    hccisId,
    isNewConversation,
    organizationId,
    organizationName,
    participantIds,
    organizationTypeId,
    conversationSubject,
    conversationMessage,
    conversationMessageBody,
    conversationTypeId,
    exceptionId,
    componentIds,
    organizationTypes,
    conversationContacts,
    availableMessageNotes,
    conversationMessages,
    createdByAndDate,
    createDate,
    isClosed,
    newMessage,
    conversations,
    conversationsCriteria,

    isGeneralNotice,
    participantNames,
    canAddMessage,

    getValidationRules,
    getConversationMessageValidationRules,
    getNewGeneralNoticeValidationRules,
    fetchConversation,
    fetchConversations,
    initializeNewConversation,
    submitConversation,
    submitGeneralNotice,
    setActiveConversationParameters,
    saveConversationMessage,
    initializeNewMessage,
    loadAvailableMessageNotes,
    updateConversationStatus,
    markAsRead,
    refreshConversationMessages,
    resetStore,
  };

},
{
  persist: {
    storage: {
      getItem: key => {
        const data = ls.get(key);
        return typeof data === 'string' ? data : JSON.stringify(data);
      },
      setItem: (key, value) => {
        ls.set(key, value);
      },
      removeItem: key => {
        ls.remove(key);
      }
    }
  }
});